// ============================================================
// Report — รายงานรวมของหน้ารายงานสรุปผล
// ------------------------------------------------------------
// สร้างเอกสาร PDF ด้วย pdfmake แล้วเปิดในแท็บใหม่ด้วย openPdf()
// ตั้งค่า pdfmake + ฟอนต์ไทย อยู่ที่ documents/pdf.js (ใช้ร่วมกันทุกไฟล์ในโฟลเดอร์นี้)
// ============================================================

import { openPdf as openPdfFile } from "../pdf.js";
import { loading, total, toFixed, format_date, getStorage, replaceText } from "../../shared/index.js";

import "../../assets/css/bootstrap.css";
import "../../shared/config/global.js";

// NOTE ยอดรวมท้ายตารางหลักล้าน (เช่น "4,539,698.41") ยาวเกินความกว้างคอลัมน์ pdfmake จึงตัดขึ้นบรรทัดใหม่
//      ตัวช่วยนี้ลดขนาดตัวอักษร "เฉพาะช่องตัวเลขในแถวสุดท้ายของตาราง ที่ยาวเกินช่อง" ให้อยู่บรรทัดเดียว
//      ไม่แตะค่าตัวเลข / ความกว้างคอลัมน์ / ช่องที่พอดีอยู่แล้ว
// ความกว้างตัวอักษร THSarabunNew (หน่วย em): ตัวเลข 0.362, "-" 0.216, "," และ "." 0.162
const PAGE_WIDTH_A4 = { portrait: 595.28, landscape: 841.89 };
const CELL_OFFSET = 9; // padding ซ้าย-ขวา 4+4 + เส้นแบ่งคอลัมน์ 1 (ค่าเริ่มต้นของ pdfmake)
const MIN_TOTAL_FONT_SIZE = 6;
const fitLongTotals = (doc) => {
  const [marginLeft = 0, , marginRight = marginLeft] = Array.isArray(doc.pageMargins) ? doc.pageMargins : [];
  const pageWidth = (PAGE_WIDTH_A4[doc.pageOrientation] || PAGE_WIDTH_A4.portrait) - marginLeft - marginRight;
  const baseFontSize = doc.defaultStyle?.fontSize || 9;
  (doc.content || []).forEach((node) => {
    const widths = node?.table?.widths;
    const body = node?.table?.body;
    if (!Array.isArray(widths) || !body?.length) return;
    const innerWidth = pageWidth - widths.length * CELL_OFFSET - 1;
    body[body.length - 1].forEach((cell, i) => {
      if (!cell || typeof cell !== "object" || typeof cell.text !== "string" || !/^-?[\d,]+(\.\d+)?$/.test(cell.text)) return;
      const span = cell.colSpan || 1;
      let cellWidth = (span - 1) * CELL_OFFSET;
      for (let k = i; k < i + span; k++) cellWidth += (parseFloat(widths[k]) * innerWidth) / 100;
      if (!(cellWidth > 0)) return;
      const em = [...cell.text].reduce((sum, ch) => sum + (/\d/.test(ch) ? 0.362 : ch === "-" ? 0.216 : 0.162), 0);
      const fontSize = cell.fontSize || baseFontSize;
      const fitSize = Math.floor(((cellWidth * 0.95) / em) * 2) / 2; // เผื่อ 5% ปัดลงทีละ 0.5pt
      if (fitSize < fontSize) cell.fontSize = Math.max(MIN_TOTAL_FONT_SIZE, fitSize);
    });
  });
  return doc;
};
const openPdf = (docDefinition, filename) => openPdfFile(fitLongTotals(docDefinition), filename);

// ปิด loading ทันทีตั้งแต่เข้ามาโหลดหน้า/ไฟล์นี้
loading(false);

// รายงานหนี้ค้างชำระ
async function report1(data, end_date) {
  loading(true);
  let t = { unit: 0, water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };
  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let meter = item.meter_connect_cost || 0;
    
    t.unit += Number(item.unit_used || 0); t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0;
    t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0; t.other += other + meter;
    t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.late += item.late_payment || 0; t.amt += item.total_amount || 0;

    return [
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" },
      { text: item.full_name || "-", alignment: "left" }, { text: item.installment || "0", alignment: "center" },
      { text: "เดือน", alignment: "center" }, { text: item.unit_used || "0", alignment: "center" },
      { text: toFixed(item.water_bill || 0), alignment: "center" }, { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" },
      { text: toFixed(item.common_expenses || 0), alignment: "center" }, { text: toFixed(item.waste_cost || 0), alignment: "center" },
      { text: toFixed(other + meter), alignment: "center" }, { text: toFixed(item.add_debt || 0), alignment: "center" },
      { text: toFixed(item.reduce_debt || 0), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed(item.total_amount || 0), alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: `รายงานแจ้งหนี้ค่าน้ำและค่าส่วนกลางค้างชำระ ณ วันที่ ${format_date(end_date || new Date(), "dd mmmm yyyy")}`, alignment: "left", fontSize: 14, bold: true },
      { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["3.64%", "6.14%", "17.68%", "3.64%", "4.64%", "5.14%", "7.64%", "6.64%", "8.14%", "7.14%", "6.14%", "4.14%", "4.14%", "8%", "7.14%"],
          body: [
            [{ text: "ลำดับ", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "เดือนที่ค้างชำระ", alignment: "center", colSpan: 2 }, "", { text: "หน่วยใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "เงินทุนอื่น", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center", colSpan: 2 }, "", { text: "ค่าปรับล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 5, alignment: "left" }, "", "", "", "", { text: t.unit.toString(), alignment: "center" }, { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center", colSpan: 2 }, "", { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4",
    pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานหนี้ค้างชำระ ${new Date().toISOString()}.pdf`);
}

// รายงานสถานะห้องชุด
async function report2(data, start_date, status_transfer) {
  loading(true);
  let month1 = start_date ? new Date(start_date) : "";
  let t = { unit: 0, water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };
  
  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let meter = item.meter_connect_cost || 0;
    
    t.unit += Number(item.unit_used || 0); t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0;
    t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0; t.other += other + meter;
    t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.late += item.late_payment || 0; t.amt += item.total_amount || 0;

    return [
      { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.current_gauge_number || "0", alignment: "center" }, { text: item.last_gauge_number || "0", alignment: "center" },
      { text: item.unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(other + meter), alignment: "center" },
      { text: toFixed((item.add_debt || 0) - (item.reduce_debt || 0)), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed(item.total_amount || 0), alignment: "center" },
      { text: item.add_on_add_debt_remark ? `${item.add_on_add_debt_remark.slice(0,7)}...` : item.remark_add_debt ? `${item.remark_add_debt.slice(0,7)}...` : "", alignment: "center" },
      { text: item.add_on_reduce_debt_remark ? `${item.add_on_reduce_debt_remark.slice(0,7)}...` : item.remark_reduce_debt ? `${item.remark_reduce_debt.slice(0,7)}...` : "", alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true }, { text: " " },
      { text: `รายงานสถานะห้องชุด ${status_transfer?.label || "ทั้งหมด"}${start_date ? ` เดือน${format_date(month1, "mmmm")} พ.ศ. ${format_date(month1, "yyyy")}` : ""}`, alignment: "left", fontSize: 14, bold: true },
      { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["4.14%", "12.68%", "6.14%", "6.64%", "6.14%", "6.14%", "5.14%", "7.14%", "6.14%", "5.14%", "5.14%", "5.14%", "6%", "5.14%", "6.57%", "6.57%"],
          body: [
            [{ text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "วันที่จดมาตร", alignment: "center" }, { text: "เลขมาตรครั้งนี้", alignment: "center" }, { text: "เลขมาตรครั้งก่อน", alignment: "center" }, { text: "จำนวนหน่วยที่ใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่ารักษาอุปกรณ์น้ำ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "รายรับอื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ค่าปรับล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }, { text: "เพิ่มหนี้", alignment: "center" }, { text: "ลดหนี้", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 5, alignment: "left" }, "", "", "", "", { text: toFixed(t.unit), alignment: "center" }, { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }, { text: "-", alignment: "left" }, { text: "-", alignment: "left" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15], pageOrientation: "landscape",
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานสถานะห้องชุด ${new Date().toISOString()}.pdf`);
}

// รายงานแจ้งหนี้ประจำเดือน
async function report3(data, start_date) {
  loading(true);
  let month1 = start_date ? new Date(start_date) : "";
  let month2 = start_date ? new Date(new Date(start_date).setMonth(new Date(start_date).getMonth() + 1)) : "";
  let t = { water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };
  // สรุปตามสถานะ (pay_status จาก backend: paid / written_off / unpaid)
  const st = { paid: { n: 0, amt: 0 }, written_off: { n: 0, amt: 0 }, unpaid: { n: 0, amt: 0 } };

  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let meter = item.meter_connect_cost || 0;

    t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0; t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0;
    t.other += other + meter; t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.late += item.late_payment || 0; t.amt += item.total_amount || 0;

    const stKey = st[item.pay_status] ? item.pay_status : null;
    if (stKey) { st[stKey].n += 1; st[stKey].amt += item.total_amount || 0; }

    return [
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" },
      { text: item.full_name || "-", alignment: "left" }, { text: item.last_gauge_number || "0", alignment: "center" },
      { text: item.current_gauge_number || "0", alignment: "center" }, { text: item.unit_used || "0", alignment: "center" },
      { text: toFixed(item.water_bill || 0), alignment: "center" }, { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" },
      { text: toFixed(item.common_expenses || 0), alignment: "center" }, { text: toFixed(item.waste_cost || 0), alignment: "center" },
      { text: toFixed(other + meter), alignment: "center" }, { text: toFixed(item.add_debt || 0), alignment: "center" },
      { text: toFixed(item.reduce_debt || 0), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed(item.total_amount || 0), alignment: "center" }, { text: item.pay_status_text || "-", alignment: "center", bold: item.pay_status === "unpaid" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: start_date ? `รายงานการแจ้งหนี้ประจำเดือน ${format_date(month1, "mmmm")} พ.ศ.${format_date(month1, "yyyy")} เรียกเก็บเดือน ${format_date(month2, "mmmm")} พ.ศ. ${format_date(month2, "yyyy")}` : "รายงานการแจ้งหนี้ประจำเดือน ทั้งหมด", fontSize: 14, alignment: "left", bold: true },
      { text: `ฉบับแสดงยอดแจ้งหนี้เต็ม (รวมห้องที่ชำระแล้ว) · เพิ่ม/ลดหนี้รวมรายการที่ทำตอนออกใบเสร็จ · ข้อมูลจากฐานข้อมูล ณ ${format_date(new Date(), "dd/mm/yyyy")}`, fontSize: 10, alignment: "left" },
      { text: " " },
      {
        table: {
          headerRows: 2,
          widths: ["3.2%", "5%", "12.8%", "5.6%", "5%", "7%", "6.8%", "9%", "7.8%", "5.4%", "6%", "4.6%", "5.2%", "5.2%", "6.8%", "5.6%"],
          body: [
            [{ text: " ", colSpan: 3, border: [false, false, true, true] }, "", "", { text: "เลขมาตร", alignment: "center", colSpan: 2 }, "", { text: " ", colSpan: 4, border: [true, false, false, true] }, "", "", "", { text: " ", colSpan: 7, border: [false, false, false, true] }, "", "", "", "", "", ""],
            [{ text: "ลำดับ", alignment: "center", fontSize: 8 }, { text: "บ้าน เลขที่", alignment: "center", fontSize: 8 }, { text: "ชื่อ-นามสกุล", alignment: "center", fontSize: 8 }, { text: "เดือนก่อน", alignment: "center", fontSize: 8 }, { text: "เดือนนี้", alignment: "center", fontSize: 8 }, { text: "หน่วยที่ใช้", alignment: "center", fontSize: 8 }, { text: "ค่าน้ำ ประปา", alignment: "center", fontSize: 8 }, { text: "ค่าบริการ", alignment: "center", fontSize: 8 }, { text: "ค่า ส่วนกลาง", alignment: "center", fontSize: 8 }, { text: "ค่าขยะ", alignment: "center", fontSize: 8 }, { text: "เงินทุน/อื่นๆ", alignment: "center", fontSize: 8 }, { text: "เพิ่ม/ลดหนี้", alignment: "center", fontSize: 8, colSpan: 2 }, "", { text: "ค่าปรับ ล่าช้า", alignment: "center", fontSize: 8 }, { text: "รวม (บาท)", alignment: "center", fontSize: 8 }, { text: "สถานะ", alignment: "center", fontSize: 8 }],
            ...array,
            [" ", { text: `รวมทั้งสิ้น ${data.length} รายการ`, alignment: "center", colSpan: 2 }, " ", " ", " ", " ", { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD), alignment: "center" }, { text: toFixed(t.redD), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }, { text: " ", alignment: "center" }],
          ],
        },
      },
      { text: " " },
      {
        table: {
          widths: [90, 60, 90],
          body: [
            [{ text: "ยอดแจ้งหนี้ทั้งหมด", alignment: "left" }, { text: `${data.length.toLocaleString("en-US")} ห้อง`, alignment: "right" }, { text: `${toFixed(t.amt)} บาท`, alignment: "right" }],
            [{ text: "ชำระแล้ว", alignment: "left" }, { text: `${st.paid.n.toLocaleString("en-US")} ห้อง`, alignment: "right" }, { text: `${toFixed(st.paid.amt)} บาท`, alignment: "right" }],
            ...(st.written_off.n ? [[{ text: "ตัดหนี้", alignment: "left" }, { text: `${st.written_off.n.toLocaleString("en-US")} ห้อง`, alignment: "right" }, { text: `${toFixed(st.written_off.amt)} บาท`, alignment: "right" }]] : []),
            [{ text: "ค้างชำระ", alignment: "left" }, { text: `${st.unpaid.n.toLocaleString("en-US")} ห้อง`, alignment: "right" }, { text: `${toFixed(st.unpaid.amt)} บาท`, alignment: "right" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานแจ้งหนี้ประจำเดือน ${new Date().toISOString()}.pdf`);
}

// รายงานแจ้งหนี้คงค้างย้อนหลัง
async function report4(data, start_date) {
  loading(true);
  let month1 = start_date ? new Date(start_date) : "";
  let t = { unit: 0, water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };

  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let meter = item.meter_connect_cost || 0;

    t.unit += Number(item.unit_used || 0); t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0; t.common += item.common_expenses || 0;
    t.waste += item.waste_cost || 0; t.other += other + meter; t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0;
    t.late += item.late_payment || 0; t.amt += item.total_amount || 0;

    return [
      { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.current_gauge_number || "0", alignment: "center" }, { text: item.last_gauge_number || "0", alignment: "center" },
      { text: item.unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(other + meter), alignment: "center" },
      { text: toFixed((item.add_debt || 0) - (item.reduce_debt || 0)), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed(item.total_amount || 0), alignment: "center" },
      { text: item.add_on_add_debt_remark ? `${item.add_on_add_debt_remark.slice(0,7)}...` : item.remark_add_debt ? `${item.remark_add_debt.slice(0,7)}...` : "", alignment: "center" },
      { text: item.add_on_reduce_debt_remark ? `${item.add_on_reduce_debt_remark.slice(0,7)}...` : item.remark_reduce_debt ? `${item.remark_reduce_debt.slice(0,7)}...` : "", alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true }, { text: " " },
      { text: start_date ? `รายงานแจ้งหนี้คงค้างย้อนหลัง เดือน${format_date(month1, "mmmm")} พ.ศ. ${format_date(month1, "yyyy")}` : "รายงานแจ้งหนี้คงค้างย้อนหลัง ทั้งหมด", alignment: "left", fontSize: 14, bold: true },
      { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["4.14%", "12.68%", "6.14%", "6.64%", "6.14%", "6.14%", "5.14%", "7.14%", "6.14%", "5.14%", "5.14%", "5.14%", "6%", "5.14%", "6.57%", "6.57%"],
          body: [
            [{ text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "วันที่จดมาตร", alignment: "center" }, { text: "เลขมาตรครั้งนี้", alignment: "center" }, { text: "เลขมาตรครั้งก่อน", alignment: "center" }, { text: "จำนวนหน่วยที่ใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่ารักษาอุปกรณ์น้ำ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "รายรับอื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ค่าปรับล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }, { text: "เพิ่มหนี้", alignment: "center" }, { text: "ลดหนี้", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 5, alignment: "left" }, "", "", "", "", { text: toFixed(t.unit), alignment: "center" }, { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }, { text: "-", alignment: "left" }, { text: "-", alignment: "left" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15], pageOrientation: "landscape",
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานแจ้งหนี้คงค้างย้อนหลัง ${new Date().toISOString()}.pdf`);
}

// 1 ใบเสร็จอาจอ้างถึงหลายบิลน้ำ (record_water_list) — เลขมาตร/วันที่จดมาตรต้องมาจาก "บิลล่าสุด" ใบเดียว
// (datetime_create มากสุด, เท่ากันให้ใช้ตัวท้าย list) ไม่ใช่ item.current_gauge_number/last_gauge_number/unit_used ตรง ๆ
// เพราะไม่มีอยู่ระดับใบเสร็จ (ค่าจะว่างเสมอ)
const latestRecordWater = (rwList) => {
  if (!rwList || !rwList.length) return null;
  return rwList.reduce((latest, cur) => {
    if (!latest) return cur;
    const lt = new Date(latest.datetime_create).getTime();
    const ct = new Date(cur.datetime_create).getTime();
    return ct >= lt ? cur : latest;
  }, null);
};

// รายงานใบเสร็จรับเงิน
async function report5(data) {
  loading(true);

  let array = data.map((item) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let debt = (item.add_debt || 0) - (item.reduce_debt || 0);
    let totalAmt = (item.water_bill || 0) + (item.equipment_maintenance_fee || 0) + (item.common_expenses || 0) + (item.waste_cost || 0) + other + debt + (item.late_payment || 0) + (item.meter_connect_cost || 0) + (item.change_satang || 0);
    const rw = latestRecordWater(item.record_water_list);

    return [
      { text: item.iw_number || "-", alignment: "center" }, { text: item.record_date ? format_date(item.record_date, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      // ★ วันที่จดมาตรของบิลน้ำที่ใบเสร็จนี้อ้างถึง — ห้ามใช้ item.datetime_create (เวลาบันทึกใบเสร็จ)
      { text: rw?.datetime_create ? format_date(rw.datetime_create, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: rw?.current_gauge_number || "0", alignment: "center" }, { text: rw?.last_gauge_number || "0", alignment: "center" },
      { text: item.sum_unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(other), alignment: "center" },
      { text: toFixed(debt), alignment: "center" }, { text: toFixed(item.meter_connect_cost || 0), alignment: "center" },
      { text: toFixed(item.late_payment || 0), alignment: "center" }, { text: toFixed(item.change_satang || 0), alignment: "center" },
      { text: toFixed(totalAmt), alignment: "center" },
      { text: item.add_on_add_debt_remark ? `${item.add_on_add_debt_remark.slice(0,7)}...` : item.remark_add_debt ? `${item.remark_add_debt.slice(0,7)}...` : "", alignment: "center" },
      { text: item.add_on_reduce_debt_remark ? `${item.add_on_reduce_debt_remark.slice(0,7)}...` : item.remark_reduce_debt ? `${item.remark_reduce_debt.slice(0,7)}...` : "", alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true }, { text: "รายงานใบเสร็จรับเงิน", alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["7.3%", "5%", "3.7%", "12%", "5%", "4%", "4%", "4%", "4%", "4%", "4%", "4%", "5%", "5%", "5%", "5%", "5%", "4.5%", "4.5%", "5%"],
          body: [
            [{ text: "เลขที่รับใบเสร็จ", alignment: "center" }, { text: "วันที่ใบเสร็จ\nรับเงิน", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "วันที่\nจดมาตร", alignment: "center" }, { text: "เลขมาตร เดือนก่อน", alignment: "center" }, { text: "เลขมาตร เดือนนี้", alignment: "center" }, { text: "จำนวน หน่วยที่ใช้", alignment: "center" }, { text: "ค่าน้ำ ประปา", alignment: "center" }, { text: "ค่ารักษา อุปกรณ์น้ำ", alignment: "center" }, { text: "ค่าส่วน กลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "ค่าใช้จ่าย อื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ค่าต่อกลับ มิเตอร์", alignment: "center" }, { text: "ค่าปรับชำระ ล่าช้า", alignment: "center" }, { text: "ปรับปรุง ยอดเงิน", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }, { text: "สาเหตุ เพิ่มหนี้", alignment: "center" }, { text: "สาเหตุ ลดหนี้", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 8, alignment: "left" }, "", "", "", "", "", "", "", 
              { text: toFixed(total(data, "water_bill")), alignment: "center" }, 
              { text: toFixed(total(data, "equipment_maintenance_fee")), alignment: "center" }, 
              { text: toFixed(total(data,"common_expenses")), alignment: "center" }, 
              { text: toFixed(total(data,"waste_cost")), alignment: "center" }, 
              { text: toFixed(total(data,"other_income") + total(data,"cable_cost") + total(data,"fire_cost")), alignment: "center" }, 
              { text: toFixed(total(data,"add_debt") - total(data,"reduce_debt")), alignment: "center" }, 
              { text: toFixed(total(data,"meter_connect_cost")), alignment: "center" }, 
              { text: toFixed(total(data,"late_payment")), alignment: "center" }, 
              { text: toFixed(total(data,"change_satang")), alignment: "center" }, 
              { text: toFixed(
                        total(data,"water_bill") +
                        total(data,"equipment_maintenance_fee") +
                        total(data,"common_expenses") +
                        total(data,"waste_cost") +
                        total(data,"cable_cost") +
                        total(data,"other_income") +
                        total(data,"fire_cost") +
                        (total(data,"add_debt") - total(data,"reduce_debt")) +
                        total(data,"meter_connect_cost") +
                        total(data,"late_payment") +
                        total(data,"change_satang"),
                      ), alignment: "center" }, "", ""],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15], pageOrientation: "landscape",
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานใบเสร็จรับเงิน ${new Date().toISOString()}.pdf`);
}

// รายงานลูกหนี้
async function report6(data, type) {
  loading(true);
  let t = { water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };
  
  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0; t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0;
    t.other += other; t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.late += item.late_payment || 0; t.amt += (item.total_amount || 0) + (item.overdue_amount || 0);

    return [
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.count_installment || "0", alignment: "center" }, { text: item.unit_used || "0", alignment: "center" },
      { text: toFixed(item.water_bill || 0), alignment: "center" }, { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" },
      { text: toFixed(item.common_expenses || 0), alignment: "center" }, { text: toFixed(item.waste_cost || 0), alignment: "center" },
      { text: toFixed(other), alignment: "center" }, { text: toFixed(item.add_debt || 0), alignment: "center" },
      { text: toFixed(item.reduce_debt || 0), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed((item.total_amount || 0) + (item.overdue_amount || 0)), alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      {
        columns: [
          { text: type === "0" ? `รายงานหนี้ค่าน้ำและค่าส่วนกลางค้างชำระ ณ วันที่ ${format_date(new Date(), "dd mmmm yyyy")}` : type === "1" ? "ลูกหนี้ค่าน้ำและค่าส่วนกลางค้างชำระที่คาดว่าไม่อยู่อาศัย (ใช้น้ำน้อยกว่า 1 หน่วย / ค้างชำระเกิน 1 ปี)" : "ลูกหนี้ค่าน้ำและค่าส่วนกลางค้างชำระที่คาดว่าไม่อยู่อาศัย (ใช้น้ำเฉลี่ยตั้งแต่ 1 หน่วยขึ้นไป หรือ ค้างชำระไม่เกิน 1 ปี)", alignment: "left", fontSize: 14, bold: true },
          { text: type === "0" ? "" : `รายงาน ณ วันที่ ${format_date(new Date(), "dd mmmm yyyy")}`, alignment: "right", fontSize: 14, bold: true, width: 150 },
        ],
      },
      { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["3.69%", "5.69%", "19.72%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "5%", "5%", "7.38%", "6.69%"],
          body: [
            [{ text: "ลำดับ", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "เดือนค้างชำระ", alignment: "center" }, { text: "หน่วยใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "เงินทุนอื่น", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center", colSpan: 2 }, "", { text: "ค่าปรับล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", alignment: "left", colSpan: 4 }, "", "", "", "", { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD), alignment: "center" }, { text: toFixed(t.redD), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานลูกหนี้ ${new Date().toISOString()}.pdf`);
}

// รายงานอื่นๆประจำเดือน
async function report7(data) {
  loading(true);
  let cost = 0, t_final = 0;
  let array = [];
  data.forEach((item) => {
    let itemCost = item.record_other_list?.[0]?.cost || 0;
    cost += itemCost;
    t_final += item.total_final || 0;
    let detail = item.record_other_list?.[0] ? `${item.record_other_list[0].title}${item.record_other_list[0].description ? ` ( ${item.record_other_list[0].description} ) ` : ""}` : "0";
    
    array.push([
      { text: item.address_number || item.address || "-", alignment: "center", rowSpan: 2 },
      { text: detail, alignment: "left", rowSpan: 2 },
      { text: item.record_date ? format_date(item.record_date) : "-", alignment: "center" },
      { text: "0", alignment: "center" }, { text: "0", alignment: "center" }, { text: "0", alignment: "center" },
      { text: "0.00", alignment: "center" }, { text: "0.00", alignment: "center" }, { text: "0.00", alignment: "center" },
      { text: "0.00", alignment: "center" }, { text: toFixed(itemCost), alignment: "center" }, { text: "0.00", alignment: "center" },
      { text: toFixed(item.total_final || 0), alignment: "center" },
    ]);
    array.push(["", "", { text: `วันที่รับเงิน ${format_date(item.datetime_create)} เลขที่ใบเสร็จรับเงิน ${item.iw_number}${item.address ? (item.full_name ? ` ได้รับเงินจาก ${item.full_name}` : "") : (item.agency_name ? ` ได้รับเงินจาก ${item.agency_name}` : "")} ${getStorage("full_name") || ""}`, alignment: "left", colSpan: 11 }, "", "", "", "", "", "", "", "", "", ""]);
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: "รายงานรายรับอื่นๆ ประจำเดือน", alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["6.69%", "19.72%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%", "6.69%"],
          body: [
            [{ text: "บ้านเลขที่", alignment: "center" }, { text: "รายละเอียด", alignment: "center" }, { text: "วันที่จดมาตร", alignment: "center" }, { text: "เลขมาตรเดือนนี้", alignment: "center" }, { text: "เลขมาตรเดือนก่อน", alignment: "center" }, { text: "จำนวนหน่วยที่ใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "รายรับอื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", alignment: "left", colSpan: 6 }, "", "", "", "", "", { text: "0.00", alignment: "center" }, { text: "0.00", alignment: "center" }, { text: "0.00", alignment: "center" }, { text: "0.00", alignment: "center" }, { text: toFixed(cost), alignment: "center" }, { text: "0.00", alignment: "center" }, { text: toFixed(t_final), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15], pageOrientation: "landscape",
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานรายรับอื่นๆ ${new Date().toISOString()}.pdf`);
}

// รายงานสรุปการจัดเก็บเงิน
async function report8(data) {
  loading(true);
  let array = data.map((item) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let debt = (item.add_debt || 0) - (item.reduce_debt || 0);
    let allSum = (item.water_bill || 0) + (item.equipment_maintenance_fee || 0) + (item.common_expenses || 0) + (item.waste_cost || 0) + (item.cable_cost || 0) + (item.fire_cost || 0) + (item.other_income || 0) + (item.add_debt || 0) + (item.late_payment || 0) + (item.meter_connect_cost || 0) + (item.other_income2 || 0) + (item.change_satang || 0) - (item.reduce_debt || 0);

    return [
      { text: item.year || "-", alignment: "center" }, { text: item.month || "-", alignment: "center" },
      { text: item.count_list || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(other), alignment: "center" },
      { text: toFixed(debt), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
      { text: toFixed(item.meter_connect_cost || 0), alignment: "center" }, { text: toFixed(item.other_income2 || 0), alignment: "center" },
      { text: toFixed(item.change_satang || 0), alignment: "center" }, { text: toFixed(allSum), alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: "รายงานสรุปรวมการจัดเก็บเงิน", alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["7.14%", "4.14%", "10.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.14%", "7.18%"],
          body: [
            [{ text: "ปี", alignment: "center" }, { text: "เดือน", alignment: "center" }, { text: "รายการจัดเก็บ", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "รายรับอื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ปรับล่าช้า", alignment: "center" }, { text: "ค่าต่อมิเตอร์", alignment: "center" }, { text: "ค่าอื่นๆ", alignment: "center" }, { text: "ปรับเศษ", alignment: "center" }, { text: "รวมเป็นเงิน", alignment: "center" }],
            ...array,
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานสรุปการจัดเก็บเงิน ${new Date().toISOString()}.pdf`);
}

// รายงานสรุปรวมยกเลิกใบเสร็จ
async function report9(data) {
  loading(true);
  let array = data.map((item) => [
    { text: item.year || "-", alignment: "center" }, { text: item.month || "-", alignment: "center" },
    { text: item.cancel_bill || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
    { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
    { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed((item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0)), alignment: "center" },
    { text: toFixed((item.add_debt || 0) - (item.reduce_debt || 0)), alignment: "center" }, { text: toFixed(item.late_payment || 0), alignment: "center" },
    { text: toFixed(item.meter_connect_cost || 0), alignment: "center" }, { text: toFixed(item.change_satang || 0), alignment: "center" },
    { text: toFixed(item.total_final || 0), alignment: "center" },
  ]);

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: "รายงานสรุปรวมการยกเลิกใบเสร็จ", alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.69%", "7.72%"],
          body: [
            [{ text: "ปี", alignment: "center" }, { text: "เดือน", alignment: "center" }, { text: "ยกเลิก (ฉบับ)", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "รายรับอื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ปรับล่าช้า", alignment: "center" }, { text: "ค่าต่อมิเตอร์", alignment: "center" }, { text: "ปรับเศษ", alignment: "center" }, { text: "รวมเป็นเงิน", alignment: "center" }],
            ...array,
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานยกเลิกใบเสร็จ ${new Date().toISOString()}.pdf`);
}

// รายงานการตัดหนี้
async function report10(data, type) {
  loading(true);
  let array = [];
  let t = { water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, final: 0 };

  data.forEach((item, index) => {
    item.other_income = (item.other_income || 0) + (!item.s_fire_cost ? (item.fire_cost || 0) : 0) + (!item.s_cable_cost ? (item.cable_cost || 0) : 0);
    let itemOther = item.record_other_list?.[0] ? item.record_other_list[0].cost : (item.other_income || 0);

    // ยอดของช่องที่ "ยังไม่ได้เก็บเงิน" เท่านั้น (s_<ช่อง> = true คือเก็บไปแล้ว)
    // NOTE เดิมค่าปรับชำระล่าช้าไม่ได้เช็ค s_late_payment และไม่ได้อยู่ใน total_final
    //      ทำให้แถวโชว์ total_final + late แต่ยอดท้ายตารางรวมแค่ total_final — ต่างกันเท่ากับค่าปรับทั้งใบ
    //      ตอนนี้คิดค่าปรับด้วยกติกาเดียวกับช่องอื่น และรวมไว้ใน total_final ตัวเดียว
    const unpaid = (key) => (item[`s_${key}`] ? 0 : Number(item[key] || 0));
    // เดิมใช้ (!s_add_debt || !s_reduce_debt) ครอบทั้งก้อน — เก็บไปข้างเดียวก็ยังนับทั้งคู่
    const itemDebt = unpaid("add_debt") - unpaid("reduce_debt");
    const itemLate = unpaid("late_payment");

    item.total_final =
      unpaid("water_bill") + unpaid("equipment_maintenance_fee") + unpaid("common_expenses") + unpaid("waste_cost") +
      (!item.s_other_income ? itemOther : 0) + itemDebt + itemLate;

    t.other += !item.s_other_income ? itemOther : 0; t.late += itemLate; t.final += item.total_final || 0;
    if (!item.s_water_bill) t.water += item.water_bill || 0; if (!item.s_equipment_maintenance_fee) t.eq += item.equipment_maintenance_fee || 0;
    if (!item.s_common_expenses) t.common += item.common_expenses || 0; if (!item.s_waste_cost) t.waste += item.waste_cost || 0;
    if (!item.s_add_debt) t.addD += item.add_debt || 0; if (!item.s_reduce_debt) t.redD += item.reduce_debt || 0;

    array.push([
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.record_water_list?.current_gauge_number || "0", alignment: "center" }, { text: item.record_water_list?.last_gauge_number || "0", alignment: "center" },
      { text: item.sum_unit_used || "0", alignment: "center" }, { text: !item.s_water_bill && item.water_bill ? toFixed(item.water_bill) : "0.00", alignment: "center" },
      { text: !item.s_equipment_maintenance_fee && item.equipment_maintenance_fee ? toFixed(item.equipment_maintenance_fee) : "0.00", alignment: "center" },
      { text: !item.s_common_expenses && item.common_expenses ? toFixed(item.common_expenses) : "0.00", alignment: "center" },
      { text: !item.s_waste_cost && item.waste_cost ? toFixed(item.waste_cost) : "0.00", alignment: "center" },
      { text: !item.s_other_income ? toFixed(itemOther) : "0.00", alignment: "center" },
      { text: toFixed(itemDebt), alignment: "center" },
      { text: toFixed(itemLate), alignment: "center" }, { text: toFixed(item.total_final), alignment: "center" },
    ]);

    let cutOff = item._rw?.cutOffDebt || item.record_water_list?.cut_off_debt;
    let causeText = cutOff?.ref_cause === 1 ? "ลูกค้าชำระแล้ว" : cutOff?.ref_cause === 2 ? "ลูกค้าเปลี่ยนสัญญา (บรรจุใหม่) ห้อง กคช." : "ลูกค้าบรรจุใหม่ ซื้อขายโอนกรรมสิทธิ์";
    array.push([" ", " ", { text: `วันที่พักหนี้ : ${format_date(item.datetime_create, "dd/mm/yyyy")}`, alignment: "left" }, { text: `รายละเอียด : ${cutOff?.ref_remark || "-"}`, alignment: "left", colSpan: 6 }, " ", " ", " ", " ", " ", { text: `สาเหตุ: ${cutOff?.ref_cause ? causeText : "-"} วันที่บรรจุใหม่ : ${format_date(cutOff?.customer_datetime_update)}`, alignment: "left", colSpan: 6 }, " ", " ", " ", " ", " "]);
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true }, { text: " " },
      {
        table: {
          headerRows: 2,
          widths: ["4.14%", "5.34%", "16.94%", "8.14%", "5.14%", "5.14%", "6.14%", "7.14%", "7.14%", "8.14%", "5.14%", "5.14%", "6%", "5.14%", "5.14%"],
          body: [
            [{ text: " ", alignment: "center", colSpan: 4, border: [false, false, true, true] }, "", "", "", { text: "เลขมาตร", alignment: "center", colSpan: 2 }, "", { text: `รายงานการตัดลูกหนี้ กรณี${type || ""} ณ วันที่ ${format_date(new Date(), "dd mmmm yyyy")}`, fontSize: 12, bold: true, alignment: "right", colSpan: 9, border: [true, false, false, true] }, "", "", "", "", "", "", "", ""],
            [{ text: "ลำดับ", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "ประจำเดือน", alignment: "center" }, { text: "ครั้งนี้", alignment: "center" }, { text: "ครั้งก่อน", alignment: "center" }, { text: "หน่วยใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "เงินทุน/อื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ค่าปรับ ล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งหมด", colSpan: 7, alignment: "left" }, "", "", "", "", "", "", { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.final), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานตัดหนี้ ${new Date().toISOString()}.pdf`);
}

// รายงานแสดงรายละเอียดยกเลิกใบเสร็จ
async function report11(data) {
  loading(true);
  let t = { water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, meter: 0, late: 0, change: 0, all: 0 };
  
  let array = data.map((item) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let listCost = item.record_other_list?.[0] ? item.record_other_list[0].cost : other;
    let fin = item.record_other_list?.[0] ? item.record_other_list[0].cost : (item.total_final || 0);

    t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0; t.common += item.common_expenses || 0;
    t.waste += item.waste_cost || 0; t.other += listCost; t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0;
    t.meter += item.meter_connect_cost || 0; t.late += item.late_payment || 0; t.change += item.change_satang || 0; t.all += fin;

    return [
      { text: item.iw_number || "-", alignment: "center" }, { text: item.record_date ? `${format_date(item.record_date, "dd/mm/yyyy")} ${item.record_time || ""}` : "-", alignment: "center" },
      { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.record_date ? `${format_date(item.record_date, "dd/mm/yyyy")} ${item.record_time || ""}` : "-", alignment: "center" },
      { text: item.record_water_list?.[0]?.current_gauge_number || "0", alignment: "center" }, { text: item.record_water_list?.[0]?.last_gauge_number || "0", alignment: "center" },
      { text: item.sum_unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(listCost), alignment: "center" },
      { text: toFixed((item.add_debt || 0) - (item.reduce_debt || 0)), alignment: "center" }, { text: toFixed(item.meter_connect_cost || 0), alignment: "center" },
      { text: toFixed(item.late_payment || 0), alignment: "center" }, { text: toFixed(item.change_satang || 0), alignment: "center" },
      { text: toFixed(fin), alignment: "center" }, { text: item.delete_remark || "", alignment: "start" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: "รายงานแสดงรายละเอียดยกเลิกใบเสร็จ", alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["8%", "5%", "4%", "8%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%", "5%"],
          body: [
            [{ text: "เลขที่รับใบ เสร็จ", alignment: "center" }, { text: "วันที่ใบเสร็จรับเงิน", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "วันที่\nจดมาตร", alignment: "center" }, { text: "เลขมาตร เดือนก่อน", alignment: "center" }, { text: "เลขมาตร เดือนนี้", alignment: "center" }, { text: "จำนวน หน่วยที่ใช้", alignment: "center" }, { text: "ค่าน้ำ ประปา", alignment: "center" }, { text: "ค่ารักษา อุปกรณ์น้ำ", alignment: "center" }, { text: "ค่าส่วน กลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "ค่าใช้จ่าย อื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "ค่าต่อกลับ มิเตอร์", alignment: "center" }, { text: "ค่าปรับชำระล่าช้า", alignment: "center" }, { text: "ปรับปรุงยอดเงิน", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }, { text: "หมายเหตุ", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 8, alignment: "left" }, "", "", "", "", "", "", "", { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center" }, { text: toFixed(t.meter), alignment: "center" }, { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.change), alignment: "center" }, { text: toFixed(t.all), alignment: "center" }, ""],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15], pageOrientation: "landscape",
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายละเอียดยกเลิกใบเสร็จ ${new Date().toISOString()}.pdf`);
}

// รายงานการลูกหนี้-โครงการ
async function report12(data, type) {
  loading(true);
  let t = { unit: 0, water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, amt: 0 };
  let array = [];

  data.forEach((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let listCost = item.record_other_list?.[0] ? item.record_other_list[0].cost + (item.meter_connect_cost || 0) : other + (item.meter_connect_cost || 0);

    t.unit += Number(item.sum_unit_used || 0); t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0;
    // คอลัมน์ "อื่นๆ" ของแถวคือ listCost (รวมค่าต่อกลับมิเตอร์) ยอดรวมท้ายตารางต้องใช้ตัวเดียวกัน
    // NOTE เดิมรวมแค่ other ยอดคอลัมน์ท้ายตารางจึงต่ำกว่าผลบวกของแถวเท่ากับค่าต่อกลับมิเตอร์ทั้งหมด
    t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0; t.other += listCost;
    t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.amt += item.total_amount || 0;

    array.push([
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.datetime_create ? format_date(item.datetime_create, "dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.record_water_list?.current_gauge_number || "0", alignment: "center" }, { text: item.record_water_list?.last_gauge_number || "0", alignment: "center" },
      { text: item.sum_unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(listCost), alignment: "center" },
      { text: toFixed((item.add_debt || 0) - (item.reduce_debt || 0)), alignment: "center" }, { text: toFixed(item.total_amount || 0), alignment: "center" },
    ]);

    let cutOff = item._rw?.cutOffDebt || item.record_water_list?.cut_off_debt;
    let causeText = cutOff?.ref_cause === 1 ? "ลูกค้าชำระแล้ว" : cutOff?.ref_cause === 2 ? "ลูกค้าเปลี่ยนสัญญา (บรรจุใหม่) ห้อง กคช." : "ลูกค้าบรรจุใหม่ ซื้อขายโอนกรรมสิทธิ์";
    array.push([" ", " ", { text: `วันที่ตัดลูกหนี้ : ${format_date(item.datetime_create, "dd/mm/yyyy")}`, alignment: "left" }, { text: `รายละเอียด : ${cutOff?.ref_remark || "-"}`, alignment: "left", colSpan: 6 }, " ", " ", " ", " ", " ", { text: `สาเหตุ: ${cutOff?.ref_cause ? causeText : "-"} วันที่บรรจุใหม่ : ${format_date(cutOff?.customer_datetime_update)}`, alignment: "left", colSpan: 5 }, " ", " ", " ", " "]);
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true }, { text: " " },
      {
        table: {
          headerRows: 2,
          widths: ["4.14%", "5.34%", "16.94%", "8.14%", "5.14%", "5.14%", "6.14%", "7.14%", "7.14%", "8.14%", "5.14%", "7.14%", "7.14%", "7.14%"],
          body: [
            [{ text: " ", alignment: "center", colSpan: 4, border: [false, false, true, true] }, "", "", "", { text: "เลขมาตร", alignment: "center", colSpan: 2 }, "", { text: `รายงานการตัดลูกหนี้ กรณี${type || ""} ณ วันที่ ${format_date(new Date(), "dd mmmm yyyy")}`, fontSize: 12, bold: true, alignment: "right", colSpan: 8, border: [true, false, false, true] }, "", "", "", "", "", "", ""],
            [{ text: "ลำดับ", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "ประจำเดือน", alignment: "center" }, { text: "ครั้งนี้", alignment: "center" }, { text: "ครั้งก่อน", alignment: "center" }, { text: "หน่วยใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "เงินทุน/อื่นๆ", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 6, alignment: "left" }, "", "", "", "", "", { text: t.unit.toString(), alignment: "center" }, { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `รายงานตัดลูกหนี้โครงการ ${new Date().toISOString()}.pdf`);
}

// รายงานลูกหนี้คงค้าง ณ วันที่
async function report13(data, end_date) {
  loading(true);
  let t = { unit: 0, water: 0, eq: 0, common: 0, waste: 0, other: 0, addD: 0, redD: 0, late: 0, amt: 0 };
  
  let array = data.map((item, index) => {
    let other = (item.other_income || 0) + (item.fire_cost || 0) + (item.cable_cost || 0);
    let meter = item.meter_connect_cost || 0;

    t.unit += Number(item.unit_used || 0); t.water += item.water_bill || 0; t.eq += item.equipment_maintenance_fee || 0;
    t.common += item.common_expenses || 0; t.waste += item.waste_cost || 0; t.other += other + meter;
    t.addD += item.add_debt || 0; t.redD += item.reduce_debt || 0; t.late += item.late_payment || 0; t.amt += item.total_amount || 0;

    return [
      { text: index + 1, alignment: "center" }, { text: item.address_number || "-", alignment: "center" }, { text: item.full_name || "-", alignment: "left" },
      { text: item.datetime_create ? format_date(item.datetime_create,"dd/mm/yyyy") : "-", alignment: "center" },
      { text: item.unit_used || "0", alignment: "center" }, { text: toFixed(item.water_bill || 0), alignment: "center" },
      { text: toFixed(item.equipment_maintenance_fee || 0), alignment: "center" }, { text: toFixed(item.common_expenses || 0), alignment: "center" },
      { text: toFixed(item.waste_cost || 0), alignment: "center" }, { text: toFixed(other + meter), alignment: "center" },
      { text: toFixed(item.add_debt || 0), alignment: "center" }, { text: toFixed(item.reduce_debt || 0), alignment: "center" },
      { text: toFixed(item.late_payment || 0), alignment: "center" }, { text: toFixed(item.total_amount || 0), alignment: "center" },
    ];
  });

  const docDefinition = {
    content: [
      { text: getStorage("project_name") || "", alignment: "left", fontSize: 18, bold: true },
      { text: `รายงานลูกหนี้คงค้าง ณ เดือน ${format_date(end_date , "dd mmmm yyyy")}`, alignment: "left", fontSize: 14, bold: true }, { text: " " },
      {
        table: {
          headerRows: 1,
          widths: ["3.64%", "6.78%", "17.68%", "7.64%", "5.14%", "7.64%", "6.64%", "8.14%", "7.14%", "7.14%", "4.14%", "4.14%", "7%", "7.14%"],
          body: [
            [{ text: "ลำดับ", alignment: "center" }, { text: "บ้านเลขที่", alignment: "center" }, { text: "ชื่อ-นามสกุล", alignment: "center" }, { text: "วันที่จดมาตร", alignment: "center" }, { text: "หน่วยใช้", alignment: "center" }, { text: "ค่าน้ำประปา", alignment: "center" }, { text: "ค่าบริการ", alignment: "center" }, { text: "ค่าส่วนกลาง", alignment: "center" }, { text: "ค่าขยะ", alignment: "center" }, { text: "เงินทุนอื่น", alignment: "center" }, { text: "เพิ่ม/ลดหนี้", alignment: "center", colSpan: 2 }, "", { text: "ค่าปรับล่าช้า", alignment: "center" }, { text: "รวม (บาท)", alignment: "center" }],
            ...array,
            [{ text: "รวมทั้งสิ้น", colSpan: 5, alignment: "left" }, "", "", "", { text: t.unit.toString(), alignment: "center" }, { text: toFixed(t.water), alignment: "center" }, { text: toFixed(t.eq), alignment: "center" }, { text: toFixed(t.common), alignment: "center" }, { text: toFixed(t.waste), alignment: "center" }, { text: toFixed(t.other), alignment: "center" }, { text: toFixed(t.addD - t.redD), alignment: "center", colSpan: 2 }, "", { text: toFixed(t.late), alignment: "center" }, { text: toFixed(t.amt), alignment: "center" }],
          ],
        },
      },
    ],
    defaultStyle: { font: "THSarabunNew", fontSize: 9, lineHeight: 0.9 },
    pageSize: "A4", pageMargins: [15, 15, 15, 15],
  };
  let textDocDefinition = await replaceText(docDefinition);
  loading(false);
  openPdf(textDocDefinition, `ลูกหนี้คงค้าง ณ วันที่ ${new Date().toISOString()}.pdf`);
}

export { report1, report2, report3, report4, report5, report6, report7, report8, report9, report10, report11, report12, report13 };